import { MAX_AGE, MIN_AGE } from '@kamernet/constants/Search';
import {
  AvailabilityPeriodEnum,
  Furnishings,
  GenderEnum,
  RentalPriceID,
  RoommateNumberID,
  SearchView,
  Statuses,
  SuitableForNumberID,
  SurfaceMinimumID,
} from '@kamernet/core/ApiClient/Kamernet';
import { Locale } from '@kamernet/core/Intl/types';
import {
  filterArrayByAllowedValues,
  valueExistsInArray,
} from '@kamernet/utilities/Array';
import {
  parseRouteParamToBoolean,
  parseRouteParamToEnum,
  parseRouteParamToEnumArray,
  parseRouteParamToInt,
} from '@kamernet/utilities/URL';

import { defineRoute } from '../defineRoute';

export const TenantsSearchAllowedSuitableForGenders = [
  GenderEnum.Male,
  GenderEnum.Female,
] as const;

export const TenantsSearchAllowedRoommateGenders = [
  GenderEnum.Male,
  GenderEnum.Female,
  GenderEnum.Mixed,
] as const;

export const TenantsSearchAllowedRoommateNumberMaxes = [
  RoommateNumberID.None,
  RoommateNumberID.Four,
  RoommateNumberID.Eight,
  RoommateNumberID.MoreThanEight,
] as const;

export const TenantsSearchAllowedSearchViews = [
  SearchView.List,
  SearchView.Favorite,
] as const;

export const AGE_RANGE = Array.from(
  { length: MAX_AGE - MIN_AGE + 1 },
  (_, i) => MIN_AGE + i,
);

export type TenantsSearchRouteParams = {
  city?: string | null;
  pageNo?: number | null;
  minSize?: SurfaceMinimumID | null;
  maxRent?: RentalPriceID | null;
  suitableForStatusIds?: Statuses[] | null;
  furnishings?: Furnishings[] | null;
  availabilityPeriods?: AvailabilityPeriodEnum[] | null;
  suitableForNumberOfPersons?: SuitableForNumberID | null;
  suitableForGenders?: GenderEnum[] | null;
  suitableForMinAge?: number | null;
  suitableForMaxAge?: number | null;
  suitableForWorkStatuses?: Statuses[] | null;
  isSmokingInsideAllowed?: boolean | null;
  isPetsInsideAllowed?: boolean | null;
  roommateNumberMax?: RoommateNumberID | null;
  roommateGenders?: GenderEnum[] | null;
  tenantsWithImageOnly?: boolean | null;
  searchView?: SearchView | null;
};

export type TenantsSearchRouteParamsString = {
  city: string;
  pageNo?: string;
  minSize?: string;
  maxRent?: string;
  searchCategories?: string;
  suitableForStatusIds?: string;
  furnishings?: string;
  availabilityPeriods?: string;
  suitableForNumberOfPersons?: string;
  suitableForGenders?: string;
  suitableForMinAge?: string;
  suitableForMaxAge?: string;
  suitableForWorkStatuses?: string;
  isSmokingInsideAllowed?: string;
  isPetsInsideAllowed?: string;
  roommateNumberMax?: string;
  roommateGenders?: string;
  tenantsWithImageOnly?: string;
  searchView?: string;
};

const COUNTRY_SLUG_BY_LOCALE: Record<Locale, string> = {
  [Locale.en]: 'netherlands',
  [Locale.nl]: 'nederland',
};

const keepAgeInRange = (age: number | null | undefined): number | null => {
  if (!age) {
    return null;
  }

  if (age < MIN_AGE) {
    return MIN_AGE;
  }

  if (age > MAX_AGE) {
    return MAX_AGE;
  }

  return age;
};

export const TenantsSearch = defineRoute<
  TenantsSearchRouteParams,
  TenantsSearchRouteParamsString
>({
  pathname: '/roommates/[city]',
  nlPathname: '/huisgenoten/[city]',
  options: { isLandlordRoute: false, isPublic: true },
  routeParamsStringifier: (routeParams, locale) => {
    const suitableForGenders = filterArrayByAllowedValues(
      routeParams.suitableForGenders || [],
      TenantsSearchAllowedSuitableForGenders,
    );

    const roommateNumberMax = valueExistsInArray(
      routeParams.roommateNumberMax,
      TenantsSearchAllowedRoommateNumberMaxes,
    )
      ? routeParams.roommateNumberMax
      : undefined;

    const roommateGenders = filterArrayByAllowedValues(
      routeParams.roommateGenders || [],
      TenantsSearchAllowedRoommateGenders,
    );

    const searchView = valueExistsInArray(
      routeParams.searchView,
      TenantsSearchAllowedSearchViews,
    )
      ? routeParams.searchView
      : undefined;

    const city = routeParams.city
      ? routeParams.city
      : COUNTRY_SLUG_BY_LOCALE[locale];

    return {
      city,
      pageNo: routeParams.pageNo?.toString(),
      minSize: routeParams.minSize?.toString(),
      maxRent: routeParams.maxRent?.toString(),
      suitableForStatusIds: routeParams.suitableForStatusIds?.join(','),
      furnishings: routeParams.furnishings?.join(','),
      availabilityPeriods: routeParams.availabilityPeriods?.join(','),
      suitableForNumberOfPersons:
        routeParams.suitableForNumberOfPersons?.toString(),
      suitableForGenders: suitableForGenders.join(','),
      suitableForMinAge: keepAgeInRange(
        routeParams.suitableForMinAge,
      )?.toString(),
      suitableForMaxAge: keepAgeInRange(
        routeParams.suitableForMaxAge,
      )?.toString(),
      suitableForWorkStatuses: routeParams.suitableForWorkStatuses?.join(','),
      isSmokingInsideAllowed: routeParams.isSmokingInsideAllowed?.toString(),
      isPetsInsideAllowed: routeParams.isPetsInsideAllowed?.toString(),
      roommateNumberMax: roommateNumberMax?.toString(),
      roommateGenders: roommateGenders.join(','),
      tenantsWithImageOnly: routeParams.tenantsWithImageOnly?.toString(),
      searchView: searchView?.toString(),
    };
  },
  routeParamsParser: routeParamsString => {
    const isCountrySearch = Object.values(COUNTRY_SLUG_BY_LOCALE).some(
      country => country === routeParamsString.city,
    );

    let suitableForGenders = parseRouteParamToEnumArray(
      routeParamsString.suitableForGenders,
      GenderEnum,
    );

    suitableForGenders = filterArrayByAllowedValues(
      suitableForGenders,
      TenantsSearchAllowedSuitableForGenders,
    );

    let searchView = parseRouteParamToEnum(
      routeParamsString.searchView,
      SearchView,
    );

    searchView = valueExistsInArray(searchView, TenantsSearchAllowedSearchViews)
      ? searchView
      : SearchView.List;

    let roommateNumberMax = parseRouteParamToEnum(
      routeParamsString.roommateNumberMax,
      RoommateNumberID,
    );

    roommateNumberMax = valueExistsInArray(
      roommateNumberMax,
      TenantsSearchAllowedRoommateNumberMaxes,
    )
      ? roommateNumberMax
      : RoommateNumberID.Empty;

    let roommateGenders = parseRouteParamToEnumArray(
      routeParamsString.roommateGenders,
      GenderEnum,
    );

    roommateGenders = filterArrayByAllowedValues(
      roommateGenders,
      TenantsSearchAllowedRoommateGenders,
    );

    return {
      city: isCountrySearch ? '' : routeParamsString.city,
      pageNo: parseRouteParamToInt(routeParamsString.pageNo) || 1,
      minSize:
        parseRouteParamToEnum(routeParamsString.minSize, SurfaceMinimumID) ||
        SurfaceMinimumID.Empty,
      maxRent:
        parseRouteParamToEnum(routeParamsString.maxRent, RentalPriceID) ||
        RentalPriceID.Empty,
      suitableForStatusIds: parseRouteParamToEnumArray(
        routeParamsString.suitableForStatusIds,
        Statuses,
      ),
      furnishings: parseRouteParamToEnumArray(
        routeParamsString.furnishings,
        Furnishings,
      ),
      availabilityPeriods: parseRouteParamToEnumArray(
        routeParamsString.availabilityPeriods,
        AvailabilityPeriodEnum,
      ),
      suitableForNumberOfPersons:
        parseRouteParamToEnum(
          routeParamsString.suitableForNumberOfPersons,
          SuitableForNumberID,
        ) || SuitableForNumberID.Empty,
      suitableForGenders,
      suitableForMinAge:
        keepAgeInRange(
          parseRouteParamToInt(routeParamsString.suitableForMinAge),
        ) || MIN_AGE,
      suitableForMaxAge:
        keepAgeInRange(
          parseRouteParamToInt(routeParamsString.suitableForMaxAge),
        ) || MAX_AGE,
      suitableForWorkStatuses: parseRouteParamToEnumArray(
        routeParamsString.suitableForWorkStatuses,
        Statuses,
      ),
      isSmokingInsideAllowed:
        parseRouteParamToBoolean(routeParamsString.isSmokingInsideAllowed) ??
        false,
      isPetsInsideAllowed:
        parseRouteParamToBoolean(routeParamsString.isPetsInsideAllowed) ??
        false,
      roommateNumberMax,
      roommateGenders,
      tenantsWithImageOnly:
        parseRouteParamToBoolean(routeParamsString.tenantsWithImageOnly) ??
        true,
      searchView,
    };
  },
});
